import get from 'lodash.get';
import set from 'lodash.set';

import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import * as BulkPurchaseActions from '../../store/bulk/purchase/bulk.purchase.actions';
import * as BulkErrorsAndWarningsActions from '../../store/bulk-errors-and-warnings/bulk-errors-and-warnings.actions';
import * as PostagePurchaseActions from '../../store/postage-purchase/postage-purchase.actions';
import {
  ConnectedActionHandlerMap as bulkCancelReducer,
  namespace as bulkCancelNamespace,
} from '../../store/bulk/cancel/bulk.cancel.reducer';
import {
  ConnectedActionHandlerMap as bulkUpdateReducer,
  namespace as bulkUpdateNamespace,
} from '../../store/bulk/update/bulk.update.reducer';
import { bulkCostReviewReducer, namespace as bulkCostReviewNamespace } from '../../store/bulk/cost-review/bulk.cost-review.reducer';
import { CostReviewHelperService } from '../../services/cost-review-helper-service';

import * as Actions from './bulk.actions';
import { reduce as bulkLabelReducer, namespace as bulkLabelNamespace } from './bulk.label.reducer';
import { calculateNextCostReviewState } from '../cost-review/wallet-costs-helper';

const initialState = {
  bulkFlowStarted: false,
  jobsToValidate: [],
  bulkBatches: [],
  shipDate: undefined,
  purchasePostageLoading: false,
  activeModalStep: undefined,
};

const updateWalletPurchaseAmount = (state, updateAction) => {
  const { jobGuid: costReviewJobGuid, walletProviderCost, amount } = updateAction;

  const costReviewData = state[costReviewJobGuid]?.costReviewData;

  if (!costReviewData) {
    return state;
  }

  return {
    ...state,
    [costReviewJobGuid]: {
      ...state[costReviewJobGuid],
      costReviewData: calculateNextCostReviewState(costReviewData, walletProviderCost.walletId, amount),
    },
  };
};

const updateInsuranceOrProviderAmount = (state, updateAction) => {
  const costReviewJobGuid = get(updateAction, 'jobGuid');
  const provider = get(updateAction, 'provider');
  const amount = get(updateAction, 'amount');
  const costReviewJob = state[costReviewJobGuid];

  if (!costReviewJob) {
    return state;
  }

  const insuranceCosts = get(costReviewJob, 'costReviewData.insuranceCosts', emptyArray);
  const providerCosts = get(costReviewJob, 'costReviewData.providerCosts', emptyArray);
  const providerToUpdate = providerCosts.find((prov) => prov.sellerLabelProviderId === get(provider, 'sellerLabelProviderId'));
  set(providerToUpdate, 'purchaseAmount', amount);
  const insuranceToUpdate = insuranceCosts.find((prov) => prov.insuranceProviderId === get(provider, 'insuranceProviderId'));
  set(insuranceToUpdate, 'purchaseAmount', amount);

  return {
    ...state,
    [costReviewJobGuid]: {
      ...state[costReviewJobGuid],
      costReviewData: {
        ...state[costReviewJobGuid].costReviewData,
        insuranceCosts,
        providerCosts,
      },
    },
  };
};

const setSellerProviderError = (state, action) => {
  const costReviewJobGuid = get(action, 'jobGuid');
  const sellerLabelProviderId = get(action, 'sellerLabelProviderId');
  const error = get(action, 'error');
  const job = state[costReviewJobGuid];
  if (!job) {
    return {
      ...state,
      purchasePostageLoading: false,
    };
  }
  const { providerCosts } = job.costReviewData;
  const adjustedProviders = providerCosts.map((provider) => ({
    ...provider,
    providerErrorMessage: provider.sellerLabelProviderId === sellerLabelProviderId ? error : '',
  }));
  return {
    ...state,
    purchasePostageLoading: false,
    [costReviewJobGuid]: {
      ...state[costReviewJobGuid],
      costReviewData: {
        ...state[costReviewJobGuid].costReviewData,
        providerCosts: adjustedProviders,
      },
    },
  };
};

const assignNewJobIdToExistingJob = (state, action) => {
  const { initialValidationJobId, newJobId } = action;
  const existingJob = Object.assign({}, state[initialValidationJobId]);
  existingJob.messages = [];
  existingJob.errorsAndWarnings = [];
  const { activeModalStep } = state;
  activeModalStep.jobGuid = newJobId;
  // oxlint-disable-next-line @typescript-eslint/no-dynamic-delete, no-param-reassign
  delete state[initialValidationJobId];
  return {
    ...state,
    activeModalStep,
    jobsToValidate: state.jobsToValidate.filter((jobId) => jobId !== initialValidationJobId).concat([newJobId]),
    [newJobId]: existingJob,
  };
};

const originalReduce = (state = initialState, action) => {
  const actionGuid = get(action, 'jobGuid');
  const costReviewData = get(action, 'costReviewData');
  const shipDate = get(action, 'shipDate');
  const { errorsAndWarnings } = action;

  switch (action.type) {
    case PostagePurchaseActions.POSTAGE_AND_INSURANCE_PURCHASE_REQUEST:
      return {
        ...state,
        purchasePostageLoading: true,
      };
    case PostagePurchaseActions.POSTAGE_PURCHASE_REQUEST_FAILURE:
      return setSellerProviderError(state, action);
    case PostagePurchaseActions.POSTAGE_AND_INSURANCE_PURCHASE_FAILURE:
      return {
        ...state,
        purchasePostageLoading: false,
        InsurancePurchaseErrorMessage: action.errorMessage,
      };
    case PostagePurchaseActions.CLOSE_ORDER_DETAILS_MODAL_ON_BILLING_INFO_LINK_CLICK:
      return {
        ...state,
        InsurancePurchaseErrorMessage: undefined,
      };
    case PostagePurchaseActions.POSTAGE_PURCHASE_DEFERRED:
    case PostagePurchaseActions.POSTAGE_AND_INSURANCE_PURCHASE_SUCCESS:
      return {
        ...state,
        purchasePostageLoading: false,
        error: undefined,
      };
    case BulkPurchaseActions.BULK_PURCHASE_START:
      return {
        ...state,
        bulkFlowStarted: true,
      };
    case BulkPurchaseActions.BULK_PURCHASE_END:
      return {
        ...state,
        jobsToValidate: emptyArray,
        bulkFlowStarted: false,
        lbsModalId: undefined,
        error: undefined,
      };
    case Actions.BULK_RESET_FOR_ERRORS_AND_WARNINGS:
      return {
        ...state,
        jobsToValidate: state.jobsToValidate.includes(actionGuid) ? state.jobsToValidate : state.jobsToValidate.concat([actionGuid]),
        [actionGuid]: {
          ...get(state, actionGuid),
          included: get(action, 'includedFulfillmentPlanIds', emptyArray),
          excluded: get(action, 'excludedFulfillmentPlanIds', emptyArray),
          andPrint: action.andPrint,
          forceErrorStep: true,
        },
      };
    case Actions.BULK_VALIDATE_REQUEST_SUCCESS:
      return {
        ...state,
        jobsToValidate: state.jobsToValidate.includes(actionGuid) ? state.jobsToValidate : state.jobsToValidate.concat([actionGuid]),
        [actionGuid]: {
          ...get(state, actionGuid),
          included: get(action, 'includedFulfillmentPlanIds', emptyArray),
          excluded: get(action, 'excludedFulfillmentPlanIds', emptyArray),
          andPrint: action.andPrint,
        },
      };
    case Actions.BULK_VALIDATE_ASSIGN_NEW_JOB_ID:
      return assignNewJobIdToExistingJob(state, action);
    case Actions.BULK_VALIDATE_REQUEST_FAILURE:
    case Actions.BULK_BATCH_VALIDATED_EVERYTHING_IS_ERRORABLE:
    case BulkErrorsAndWarningsActions.CANCEL_BULK_ERRORS_AND_WARNINGS_MODAL:
      return {
        ...state,
        bulkFlowStarted: false,
      };
    case Actions.BULK_BATCH_VALIDATED_CLEAR:
      return {
        ...state,
        jobsToValidate: state.jobsToValidate.filter((job) => job !== action.jobGuid),
        [actionGuid]: undefined,
      };
    case BulkPurchaseActions.BULK_PURCHASE_COST_REVIEW_OPEN:
      return {
        ...state,
        [actionGuid]: {
          costReviewData: CostReviewHelperService.mergeWalletProviders(costReviewData),
          included: get(action, 'includedFulfillmentPlanIds'),
          excluded: get(action, 'excludedFulfillmentPlanIds'),
          errored: get(action, 'erroredFulfillmentPlanIds'),
          andPrint: get(action, 'andPrint'),
          recommendationSummary: action?.recommendationSummary,
          appliedSavings: action?.appliedSavings,
        },
      };
    case Actions.BULK_START_PROCESSING_COST_REVIEW:
      return {
        ...state,
        [actionGuid]: {
          costReviewData: emptyObject,
          included: get(action, 'included'),
          excluded: get(action, 'excluded'),
          errored: get(action, 'errored'),
          andPrint: get(action, 'andPrint'),
          shouldShowLegacyCostReview: get(action, 'shouldShowLegacyCostReview', false),
          forceErrorStep: false,
        },
        shipDate: undefined,
      };
    case BulkPurchaseActions.BULK_PURCHASE_PROVIDER_PURCHASE_AMOUNT_CHANGED:
    case BulkPurchaseActions.BULK_PURCHASE_INSURANCE_PURCHASE_AMOUNT_CHANGED:
      return updateInsuranceOrProviderAmount(state, action);
    case BulkPurchaseActions.BULK_PURCHASE_WALLET_PURCHASE_AMOUNT_CHANGED:
      return updateWalletPurchaseAmount(state, action);
    case Actions.BULK_CLEAR_COST_REVIEW:
      return {
        ...state,
        [actionGuid]: undefined,
      };
    case BulkPurchaseActions.BULK_PURCHASE_COST_REVIEW_CANCEL:
    case Actions.BULK_IGNORE_SHIPSURANCE_TERMS_AND_CONDITIONS:
      return {
        ...state,
        [actionGuid]: undefined,
        bulkFlowStarted: false,
        purchasePostageLoading: false,
        InsurancePurchaseErrorMessage: undefined,
      };
    case Actions.BULK_SET_SHIP_DATE:
      return {
        ...state,
        shipDate,
      };
    case Actions.BULK_SET_ACTIVE_LABEL_BATCH_STATUS_STEP:
      return {
        ...state,
        activeModalStep: {
          ...state.activeModalStep,
          ...action.stepData,
        },
      };
    case Actions.BULK_SET_LABEL_PROCESSING_ERROR:
      return {
        ...state,
        error: action.error,
      };
    case BulkPurchaseActions.BULK_PURCHASE_RETRY_VALIDATION:
    case BulkPurchaseActions.COST_REVIEW_BULK_RETRY:
      return {
        ...state,
        error: undefined,
      };
    case Actions.BULK_UPDATE_JOB_ERROR_AND_WARNINGS:
      return {
        ...state,
        [actionGuid]: {
          ...state[actionGuid],
          errorsAndWarnings,
        },
      };
    case BulkPurchaseActions.BULK_PURCHASE_SET_LBS_MODAL_ID:
      return {
        ...state,
        lbsModalId: action.lbsModalId,
      };
    case BulkPurchaseActions.BULK_CLEAR_ACTIVE_MODAL_STEP:
      return {
        ...state,
        activeModalStep: undefined,
      };
    case BulkPurchaseActions.COST_REVIEW_APPLY_RECOMMENDATIONS:
      return {
        ...state,
        [actionGuid]: {
          ...state[actionGuid],
          isApplyingRecommendations: true,
        },
      };
    case BulkPurchaseActions.COST_REVIEW_APPLY_RECOMMENDATIONS_SUCCESS:
    case BulkPurchaseActions.COST_REVIEW_APPLY_RECOMMENDATIONS_ERROR:
      return {
        ...state,
        [actionGuid]: {
          ...state[actionGuid],
          isApplyingRecommendations: false,
        },
      };
    default:
      return state;
  }
};

export const reduce = (state, action) => ({
  ...originalReduce(state, action),
  [bulkLabelNamespace]: bulkLabelReducer(get(state, bulkLabelNamespace), action),
  [bulkUpdateNamespace]: bulkUpdateReducer(get(state, bulkUpdateNamespace), action),
  [bulkCancelNamespace]: bulkCancelReducer(get(state, bulkCancelNamespace), action),
  [bulkCostReviewNamespace]: bulkCostReviewReducer(state?.[bulkCostReviewNamespace], action),
});

export { namespace } from './bulk.reducer.metadata';
